import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { StreetLightPlannerSidebarNewScenarioComponent } from '@app/energy-map/components/street-light-planner-sidebar-new-scenario/street-light-planner-sidebar-new-scenario.component';
import { Municipality, MunicipalityService } from '@app/shared/services/api/municipality.service';
import { UserService } from '@app/shared/services/api/user.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { TranslateService } from '@ngx-translate/core';
import { map as _map, pullAllBy as _pullAllBy, toLower as _toLower, trim as _trim, uniq as _uniq, uniqBy as _uniqBy } from 'lodash';
import { Observable, Subject, forkJoin, of, map, EMPTY } from 'rxjs';
import { catchError, first, switchMap, takeUntil, tap } from 'rxjs/operators';
import { NotificationDialogComponent } from '../../../shared/dialog/notification-dialog/notification-dialog.component';
import { ScenarioPlannerService } from '../../../shared/services/api/scenario-planner.service';
import { SidebarService } from '../../../shared/services/sidebar.service';
import { ScenarioResponseDto } from '@app/shared/models/scenario-planner';
import { User } from '@app/shared/models/user';

@Component({
  selector: 'app-load-scenario-dialog',
  templateUrl: './load-scenario-dialog.component.html',
  styleUrls: ['./load-scenario-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoadScenarioDialogComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  public scenarioList: ScenarioResponseDto[] = [];

  constructor(
    private matDialog: MatDialog,
    private matDialogRef: MatDialogRef<LoadScenarioDialogComponent>,
    private municipalityApi: MunicipalityService,
    private router: Router,
    private scenarioPlannerService: ScenarioPlannerService,
    private sidebarService: SidebarService,
    private translate: TranslateService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private usersApi: UserService
  ) {}

  public ngOnInit(): void {
    this.loadScenarioLists();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public loadScenario(id: string): void {
    this.router.navigate(['energiekarte', id]);
    this.matDialogRef.close();
  }

  public deleteScenario(id: string): void {
    const matRef = this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: this.translate.instant('module.energyMap.scenarioPanel.loadScenarioDialog.deleteDescriptionText'),
        title: this.translate.instant('module.energyMap.scenarioPanel.loadScenarioDialog.deleteTitleText'),
        confirmText: this.translate.instant('module.modals.confirmation.confirmationButtonText'),
        declineText: this.translate.instant('module.modals.confirmation.negationButtonText'),
      },
      panelClass: ['-md'],
    });

    matRef.afterClosed().subscribe((res: boolean) => {
      if (res) {
        this.scenarioPlannerService
          .delete(id)
          .pipe(first())
          .subscribe(() => {
            _pullAllBy(this.scenarioList, [{ plannerScenarioId: id }], 'plannerScenarioId');
          });
      }
    });
  }

  public openModal(): void {
    this.sidebarService.showComponent(StreetLightPlannerSidebarNewScenarioComponent);
    this.matDialogRef.close();
  }

  public trackByFn(_: number, item: any): string {
    return item.plannerScenarioId;
  }

  private getMunicipality(guid: string): Observable<Municipality[]> {
    return this.municipalityApi.getByMunicipalityGuid(guid).pipe(
      catchError(() => {
        return of([] as Municipality[]);
      })
    );
  }

  private getScenarioDetails(): Observable<[Municipality[], User[]]> {
    const municipalities = _uniqBy(this.scenarioList, 'municipalityId');
    const createdByIds = _map(this.scenarioList, 'createdBy');
    const updatedByIds = _map(this.scenarioList, 'updatedBy');
    const ids = _uniq(createdByIds.concat(updatedByIds));

    const municipalitiesCheck$ =
      municipalities.length > 0 ? forkJoin(municipalities.map((m) => this.getMunicipality(m.municipalityId))).pipe(map((results) => results.flat())) : of([]);

    const usersCheck$ =
      ids.length > 0 ? forkJoin(ids.map((id) => this.getUser(id))).pipe(map((results) => results.filter((user): user is User => user !== null))) : of([]);

    return forkJoin([municipalitiesCheck$, usersCheck$]);
  }

  private getUser(id: string): Observable<User | null> {
    return this.usersApi.getNameByGuid(id).pipe(
      map((user) => ({ ...user, guid: id } as User)),
      catchError(() => {
        return of(null);
      })
    );
  }

  private getUserDisplayName(users: User[], userId: string): string {
    const user = users.find((u) => _trim(_toLower(u.guid)) === _trim(_toLower(userId)));

    if (user && (user.firstname || user.lastname)) {
      const firstname = user.firstname || '';
      const lastname = user.lastname || '';
      return `${firstname} ${lastname}`.trim();
    }

    return '';
  }

  private loadScenarioLists(): void {
    const municipalityId = this.userMunicipalitiesService.currentMunicipality.guid;

    this.scenarioPlannerService
      .getByMunicipalityId(municipalityId)
      .pipe(
        tap((scenarios: ScenarioResponseDto[]) => {
          if (scenarios.length === 0) {
            this.matDialogRef.close({ displayNoResult: true });
            return;
          }
          this.scenarioList = scenarios;
        }),
        switchMap((scenarios: ScenarioResponseDto[]) => (scenarios.length > 0 ? this.getScenarioDetails() : EMPTY)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((response: [Municipality[], User[]]) => {
        const [municipalities, users] = response;
        this.updateScenariosWithDetails(municipalities, users);
      });
  }

  private updateScenariosWithDetails(municipalities: Municipality[], users: User[]): void {
    this.scenarioList.forEach((scenario) => {
      const municipality = municipalities.find((m) => m.guid === scenario.municipalityId);
      if (municipality) {
        scenario['municipalityName'] = municipality.name;
      }
      scenario['createdByName'] = this.getUserDisplayName(users, scenario.createdBy);
      scenario['modifiedByName'] = this.getUserDisplayName(users, scenario.updatedBy);
    });
  }
}
